import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { adminConfigAPI, adminDemoCallsAPI } from '../../lib/adminApi';
import {
  Phone, Save, RefreshCw, Clock, PhoneOutgoing, FileText,
  ShieldCheck, ShieldOff, Plus, Trash2, Loader2, Info
} from 'lucide-react';

const LANGUAGES = [
  'Hinglish', 'Hindi', 'English',
  'Tamil', 'Telugu', 'Kannada', 'Malayalam',
  'Bengali', 'Gujarati', 'Marathi', 'Punjabi',
  'Odia', 'Assamese', 'Urdu'
];

export default function AdminDemoCallsPage() {
  const [config, setConfig] = useState({
    greeting: '', script: '', system_prompt: '', language: 'Hinglish'
  });
  const [calls, setCalls] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newWhitelistPhone, setNewWhitelistPhone] = useState('');
  const [newWhitelistNote, setNewWhitelistNote] = useState('');
  const [addingWhitelist, setAddingWhitelist] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [greetRes, scriptRes, promptRes, langRes, callsRes, wlRes] = await Promise.all([
        adminConfigAPI.get('DEMO_GREETING').catch(() => ({ data: {} })),
        adminConfigAPI.get('DEMO_SCRIPT').catch(() => ({ data: {} })),
        adminConfigAPI.get('DEMO_SYSTEM_PROMPT').catch(() => ({ data: {} })),
        adminConfigAPI.get('DEMO_LANGUAGE').catch(() => ({ data: {} })),
        adminDemoCallsAPI.getCalls().catch(() => ({ data: [] })),
        adminDemoCallsAPI.getWhitelist().catch(() => ({ data: [] })),
      ]);
      setConfig({
        greeting: greetRes.data?.value || '',
        script: scriptRes.data?.value || '',
        system_prompt: promptRes.data?.value || '',
        language: langRes.data?.value || 'Hinglish',
      });
      setCalls(callsRes.data || []);
      setWhitelist(wlRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        adminConfigAPI.set('DEMO_GREETING', config.greeting),
        adminConfigAPI.set('DEMO_SCRIPT', config.script),
        adminConfigAPI.set('DEMO_SYSTEM_PROMPT', config.system_prompt),
        adminConfigAPI.set('DEMO_LANGUAGE', config.language),
      ]);
      toast.success('Demo call settings saved!');
    } catch (err) { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const handleWhitelistFromLog = async (phone) => {
    try {
      const res = await adminDemoCallsAPI.addWhitelist({ phone, note: 'Added from call logs' });
      toast.success(res.data.message);
      await loadData();
    } catch (err) { toast.error('Failed to whitelist number'); }
  };

  const handleRemoveWhitelist = async (phone) => {
    if (!window.confirm(`Remove ${phone} from whitelist?`)) return;
    try {
      await adminDemoCallsAPI.removeWhitelist(phone);
      toast.success(`${phone} removed from whitelist`);
      await loadData();
    } catch (err) { toast.error('Failed to remove from whitelist'); }
  };

  const handleAddWhitelist = async () => {
    if (!newWhitelistPhone.trim()) { toast.error('Enter a phone number'); return; }
    setAddingWhitelist(true);
    try {
      const res = await adminDemoCallsAPI.addWhitelist({
        phone: newWhitelistPhone.trim(),
        note: newWhitelistNote.trim()
      });
      toast.success(res.data.message);
      setNewWhitelistPhone('');
      setNewWhitelistNote('');
      await loadData();
    } catch (err) { toast.error('Failed to add to whitelist'); }
    finally { setAddingWhitelist(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-white/40">Loading...</div>;

  return (
    <div className="space-y-8" data-testid="admin-demo-calls-page">
      {/* Settings Section */}
      <div>
        <h2 className="text-xl font-bold text-white mb-1">Demo Call Settings</h2>
        <p className="text-sm text-white/40 mb-6">Configure what the AI says when someone tries a demo call from the landing page.</p>

        <div className="bg-[#0B1221] border border-white/[0.06] rounded-xl p-6 space-y-5">
          <div className="p-3 rounded-lg bg-amber-600/[0.05] border border-amber-500/20 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-400">
              If System Prompt is filled, the AI will use your custom instructions. If left blank, the AI will use its pre-trained DotCall sales pitch (Hindi-first, bilingual).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Demo Language</Label>
              <Select value={config.language} onValueChange={(v) => setConfig({ ...config, language: v })}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="demo-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 max-h-60">
                  {LANGUAGES.map(l => <SelectItem key={l} value={l} className="text-white hover:bg-white/5">{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Greeting Message</Label>
              <Input value={config.greeting} onChange={(e) => setConfig({ ...config, greeting: e.target.value })}
                placeholder="Namaste! Main DotCall AI bol rahi hoon..."
                data-testid="demo-greeting-input"
                className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
            </div>
          </div>

          <div>
            <Label className="text-slate-300 text-sm mb-2 block">System Prompt (AI Instructions) <span className="text-slate-600">— Leave blank to use pre-trained AI</span></Label>
            <Textarea value={config.system_prompt} onChange={(e) => setConfig({ ...config, system_prompt: e.target.value })}
              placeholder="Leave blank to use DotCall's pre-trained Hindi-first bilingual sales AI. Or write custom instructions here..."
              data-testid="demo-prompt-input"
              className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 min-h-[120px] text-sm" />
          </div>

          <div>
            <Label className="text-slate-300 text-sm mb-2 block">Conversation Script</Label>
            <Textarea value={config.script} onChange={(e) => setConfig({ ...config, script: e.target.value })}
              placeholder={"Agent: Namaste! DotCall AI bol rahi hoon...\nCustomer: Haan, kya kar sakti ho?\nAgent: Main AI voice agent hoon — sales calls, support, reminders sab handle karti hoon..."}
              data-testid="demo-script-input"
              className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 min-h-[140px] text-sm" />
            <p className="text-xs text-slate-500 mt-1.5">The AI will follow this script when handling demo calls from the landing page.</p>
          </div>

          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-500 text-white rounded-lg" data-testid="demo-save-button">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Whitelist Section */}
      <div>
        <h2 className="text-xl font-bold text-white mb-1">Whitelisted Numbers</h2>
        <p className="text-sm text-white/40 mb-4">These numbers can make unlimited demo calls (bypassing 1-call-per-number limit).</p>

        <div className="bg-[#0B1221] border border-white/[0.06] rounded-xl p-5 space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-slate-300 text-sm mb-2 block">Phone Number</Label>
              <Input value={newWhitelistPhone} onChange={(e) => setNewWhitelistPhone(e.target.value)}
                placeholder="+919876543210" data-testid="whitelist-phone-input"
                className="bg-slate-950 border-white/10 text-white" />
            </div>
            <div className="flex-1">
              <Label className="text-slate-300 text-sm mb-2 block">Note (optional)</Label>
              <Input value={newWhitelistNote} onChange={(e) => setNewWhitelistNote(e.target.value)}
                placeholder="e.g. Testing, Team member"
                className="bg-slate-950 border-white/10 text-white" />
            </div>
            <Button onClick={handleAddWhitelist} disabled={addingWhitelist} data-testid="add-whitelist-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap">
              {addingWhitelist ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add
            </Button>
          </div>

          {whitelist.length === 0 ? (
            <p className="text-sm text-slate-600 text-center py-4">No whitelisted numbers. Add numbers above or click "Allow Unlimited" on call logs below.</p>
          ) : (
            <div className="space-y-2">
              {whitelist.map((wl, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-white font-mono">{wl.phone}</span>
                    {wl.note && <span className="text-xs text-slate-500">— {wl.note}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600">{wl.created_at ? new Date(wl.created_at).toLocaleDateString() : ''}</span>
                    <Button size="sm" variant="ghost" onClick={() => handleRemoveWhitelist(wl.phone)}
                      className="text-slate-500 hover:text-red-400 hover:bg-red-500/10 h-7 w-7 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Demo Call Logs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Demo Call Logs</h2>
          <Button variant="outline" size="sm" onClick={loadData} className="border-white/10 text-white/60 hover:text-white" data-testid="demo-refresh-button">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {calls.length === 0 ? (
          <div className="bg-[#0B1221] border border-white/[0.06] rounded-xl p-12 text-center">
            <Phone className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-white/30 text-sm">No demo calls yet. When someone uses "Try a Demo Call" on the landing page, calls will appear here.</p>
          </div>
        ) : (
          <div className="bg-[#0B1221] border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="demo-calls-table">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Phone</th>
                    <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Status</th>
                    <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Duration</th>
                    <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Date</th>
                    <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Transcript</th>
                    <th className="text-right text-xs text-white/40 font-medium px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call, i) => (
                    <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`demo-call-row-${i}`}>
                      <td className="px-4 py-3 text-sm text-white/70 flex items-center gap-2">
                        <PhoneOutgoing className="w-3.5 h-3.5 text-amber-400/60" />
                        {call.phone_number}
                        {call.is_whitelisted && (
                          <Badge className="bg-emerald-600/20 text-emerald-400 border-0 text-[9px] ml-1">Whitelisted</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          call.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' :
                          call.status === 'failed' ? 'bg-red-500/10 text-red-400' :
                          'bg-amber-500/10 text-amber-400'
                        }`}>
                          {call.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-white/40">
                        <Clock className="w-3 h-3 inline mr-1" />{call.duration || 0}s
                      </td>
                      <td className="px-4 py-3 text-sm text-white/40">
                        {new Date(call.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-xs text-white/30 max-w-xs truncate">
                        {call.transcript ? call.transcript.slice(0, 80) + '...' : 'No transcript'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!call.is_whitelisted ? (
                          <Button size="sm" variant="ghost"
                            onClick={() => handleWhitelistFromLog(call.phone_number)}
                            className="text-emerald-400/70 hover:text-emerald-400 hover:bg-emerald-500/10 text-xs"
                            title="Allow unlimited demo calls for this number">
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Allow Unlimited
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost"
                            onClick={() => handleRemoveWhitelist(call.phone_number)}
                            className="text-slate-500 hover:text-red-400 hover:bg-red-500/10 text-xs"
                            title="Remove from whitelist">
                            <ShieldOff className="w-3.5 h-3.5 mr-1" /> Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
